import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail, BACKEND_URL, downloadExport } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSync } from "../context/SyncContext";
import { ROLES, fmtFCFA, fmtDate } from "../lib/format";
import { EditableListSelect } from "../components/EditableListSelect";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, FileSpreadsheet, Check, X, Eye, Building2, RotateCcw, Clock, AlertTriangle, BarChart3 } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-blue-50 text-blue-700 border-blue-200",
  executed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  reconciled: "bg-emerald-50 text-emerald-700 border-emerald-200",
  debited: "bg-red-100 text-red-800 border-red-300",
};
const STATUS_LABEL = { pending: "Awaiting CEO", approved: "Approved · to execute", executed: "Executed · reconcile", reconciled: "Reconciled", debited: "Debited from salary", rejected: "Rejected" };
const EMPTY_ITEM = () => ({ name: "", quantity: 1, unit_cost: 0 });

export default function Requests() {
  const { user, isRole } = useAuth();
  const { tick } = useSync();
  const isCEO = isRole(ROLES.CEO), isFM = isRole(ROLES.FM);
  const canCreate = isRole(ROLES.ACEO, ROLES.FM, ROLES.GM);

  const [logoUrl, setLogoUrl] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ purpose: "", project_id: "", items: [EMPTY_ITEM()] });
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState(null); // request being viewed/reviewed
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [reconcile, setReconcile] = useState(null);
  const [spent, setSpent] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [nowTs, setNowTs] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNowTs(Date.now()), 1000); return () => clearInterval(t); }, []);
  const [showReport, setShowReport] = useState(false);
  const [report, setReport] = useState(null);
  const [reportMonth, setReportMonth] = useState(() => new Date().toISOString().slice(0, 7));
  useEffect(() => {
    if (!showReport) return;
    api.get("/requests/reconciliation-report", { params: { month: reportMonth } }).then(({ data }) => setReport(data)).catch(() => {});
  }, [showReport, reportMonth]);

  const submitReconcile = async () => {
    if (!spent) return toast.error("Enter the amount spent.");
    const fd = new FormData();
    fd.append("amount_spent", spent);
    if (receipt) fd.append("receipt", receipt);
    try {
      await api.patch(`/requests/${reconcile.id}/reconcile`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Reconciled — request closed"); setReconcile(null); setSpent(""); setReceipt(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const qc = useQueryClient();
  const { data: requests = [] } = useQuery({ queryKey: ["requests"], queryFn: () => api.get("/requests").then((r) => r.data) });
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: () => api.get("/projects").then((r) => r.data) });
  const { data: deductions = { total: 0, items: [] } } = useQuery({ queryKey: ["salary-deductions"], queryFn: () => api.get("/requests/salary-deductions").then((r) => r.data) });
  const load = () => qc.invalidateQueries();
  useEffect(() => {
    api.get("/companies").then(({ data }) => {
      const active = localStorage.getItem("stems_company");
      const c = data.find((x) => x.id === active) || data.find((x) => x.id !== "all") || data[0];
      if (c) { setLogoUrl(c.logo_url || ""); setCompanyName(c.name || ""); }
    }).catch(() => {});
  }, []);

  const formTotal = useMemo(
    () => form.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_cost) || 0), 0),
    [form.items]
  );

  const pendingRecon = useMemo(
    () => requests.filter((r) => r.status === "executed" && r.purpose === "Purchases" && r.created_by === user?.id && r.reconcile_due_at),
    [requests, user]
  );
  const fmtCountdown = (dueIso) => {
    const ms = new Date(dueIso).getTime() - nowTs;
    if (ms <= 0) return { text: "Overdue — salary debit imminent", danger: true };
    const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000), s = Math.floor((ms % 60000) / 1000);
    return { text: `${h}h ${m}m ${s}s left`, danger: ms < 2 * 3600000 };
  };

  const setItem = (idx, patch) => setForm((f) => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  const addRow = () => setForm((f) => f.items.length >= 5 ? f : ({ ...f, items: [...f.items, EMPTY_ITEM()] }));
  const removeRow = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const resetForm = () => setForm({ purpose: "", project_id: "", items: [EMPTY_ITEM()] });

  const submit = async () => {
    const items = form.items.filter((it) => it.name && (Number(it.quantity) || 0) > 0);
    if (!form.purpose) return toast.error("Select a purpose.");
    if (items.length === 0) return toast.error("Add at least one item with a quantity.");
    setSaving(true);
    try {
      await api.post("/requests", {
        purpose: form.purpose, project_id: form.project_id || null,
        items: items.map((it) => ({ name: it.name, quantity: Number(it.quantity) || 0, unit_cost: Number(it.unit_cost) || 0 })),
      });
      toast.success("Request submitted for CEO approval");
      setShowForm(false); resetForm(); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };

  const refreshActive = async (id) => { const { data } = await api.get(`/requests/${id}`); setActive(data); load(); };
  const rejectItem = async (rid, itemId) => { try { const { data } = await api.patch(`/requests/${rid}/items/${itemId}/reject`); setActive(data); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const restoreItem = async (rid, itemId) => { try { const { data } = await api.patch(`/requests/${rid}/items/${itemId}/restore`); setActive(data); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const approve = async (rid) => { try { await api.patch(`/requests/${rid}/approve`); toast.success("Approved — sent to FM for execution"); setActive(null); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const rejectAll = async (rid) => { try { await api.patch(`/requests/${rid}/reject`, { reason }); toast.success("Request rejected"); setRejecting(false); setReason(""); setActive(null); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const execute = async (rid) => { try { await api.patch(`/requests/${rid}/execute`); toast.success("Request executed and stored"); setActive(null); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  return (
    <div className="space-y-6" data-testid="requests-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Financial Requests</h1>
          <p className="text-sm text-slate-500">Submit purchase, transport and allowance requests for CEO approval.</p>
        </div>
        <div className="flex items-center gap-2">
          {isCEO && (
            <Button variant="outline" data-testid="recon-report-btn" onClick={() => setShowReport(true)} className="gap-2">
              <BarChart3 className="h-4 w-4" /> Reconciliation report
            </Button>
          )}
          {canCreate && (
            <Button data-testid="new-request-btn" onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#1E3A8A] hover:bg-[#1D4ED8] gap-2">
              <Plus className="h-4 w-4" /> New Request
            </Button>
          )}
        </div>
      </div>

      {pendingRecon.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3" data-testid="pending-reconciliation">
          <div className="flex items-center gap-2 text-amber-800">
            <Clock className="h-5 w-5" />
            <h2 className="text-sm font-semibold">Purchases pending reconciliation — upload a receipt before the deadline</h2>
          </div>
          <div className="space-y-2">
            {pendingRecon.map((r) => {
              const c = fmtCountdown(r.reconcile_due_at);
              return (
                <div key={r.id} data-testid={`pending-recon-${r.id}`} className="flex items-center justify-between gap-3 bg-white rounded-lg border border-amber-100 px-3 py-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800">{r.request_number} · {fmtFCFA(r.total)}</div>
                    <div className={`text-xs flex items-center gap-1 ${c.danger ? "text-red-600 font-semibold" : "text-amber-700"}`} data-testid={`countdown-${r.id}`}>
                      {c.danger && <AlertTriangle className="h-3.5 w-3.5" />}{c.text}
                    </div>
                  </div>
                  <Button size="sm" data-testid={`pending-reconcile-${r.id}`} onClick={() => setReconcile(r)} className="bg-[#1E3A8A] hover:bg-[#1D4ED8] shrink-0">Reconcile now</Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Request #</th>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Requested by</th>
              <th className="text-left px-4 py-3">Purpose</th>
              <th className="text-right px-4 py-3">Total (FCFA)</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3 sticky right-0 z-[1] bg-slate-50">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {requests.map((r) => (
              <tr key={r.id} data-testid={`request-row-${r.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-800">{r.request_number}</td>
                <td className="px-4 py-3 text-slate-600">{fmtDate(r.created_at)}</td>
                <td className="px-4 py-3 text-slate-600">{r.created_by_name} <span className="text-slate-400">· {r.created_by_role}</span></td>
                <td className="px-4 py-3 text-slate-600">{r.purpose}</td>
                <td className="px-4 py-3 text-right font-semibold text-slate-800">{fmtFCFA(r.total)}</td>
                <td className="px-4 py-3"><span className={`text-xs border rounded px-2 py-0.5 ${STATUS_BADGE[r.status]}`}>{STATUS_LABEL[r.status] || r.status}</span></td>
                <td className="px-4 py-3 text-right sticky right-0 z-[1] bg-white"><div className="stems-actions">
                  {isCEO && r.status === "pending" ? (
                    <Button size="sm" data-testid={`review-${r.id}`} onClick={() => setActive(r)} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">Review</Button>
                  ) : isFM && r.status === "approved" ? (
                    <Button size="sm" data-testid={`execute-${r.id}`} onClick={() => execute(r.id)} className="bg-emerald-600 hover:bg-emerald-700">Execute</Button>
                  ) : (
                    <Button size="sm" variant="outline" data-testid={`view-${r.id}`} onClick={() => setActive(r)} className="gap-1"><Eye className="h-3.5 w-3.5" /> View</Button>
                  )}
                  <Button size="sm" variant="ghost" title="View voucher" data-testid={`voucher-${r.id}`} onClick={() => downloadExport(`/export/request/${r.id}/pdf`, `voucher_${(r.id || "").slice(0, 8)}.pdf`)}><FileSpreadsheet className="h-3.5 w-3.5" /></Button>
                  {r.status === "executed" && r.purpose === "Purchases" && r.created_by === user?.id && (
                    <Button size="sm" variant="outline" data-testid={`reconcile-${r.id}`} onClick={() => setReconcile(r)}>Reconcile</Button>
                  )}
                </div></td>
              </tr>
            ))}
            {requests.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No requests yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {deductions.items.length > 0 && (
        <div className="bg-white rounded-xl border border-red-200 shadow-sm overflow-hidden" data-testid="salary-deductions">
          <div className="flex items-center justify-between px-4 py-3 bg-red-50 border-b border-red-100">
            <h2 className="text-sm font-semibold text-red-800">{isCEO ? "Salary deductions (all staff)" : "Your salary deductions"}</h2>
            <span className="text-sm font-bold text-red-800" data-testid="deductions-total">{fmtFCFA(deductions.total)}</span>
          </div>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-slate-100">
              {deductions.items.map((d) => (
                <tr key={d.id} data-testid={`deduction-row-${d.id}`} className="hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium text-slate-700">{d.request_number}</td>
                  {isCEO && <td className="px-4 py-2 text-slate-500">{d.created_by_name}</td>}
                  <td className="px-4 py-2 text-slate-500">{d.purpose}</td>
                  <td className="px-4 py-2 text-slate-500">{fmtDate(d.debited_at)}</td>
                  <td className="px-4 py-2 text-right font-semibold text-red-700">-{fmtFCFA(d.debited_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* New request form */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl" data-testid="request-form-dialog">
          <DialogHeader><DialogTitle>New Financial Request</DialogTitle></DialogHeader>
          <div className="flex items-center gap-3 border-b border-slate-100 pb-3">
            {logoUrl ? <img src={logoUrl} alt="logo" className="h-11 w-11 rounded-lg object-cover" /> : <div className="h-11 w-11 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center"><Building2 className="h-6 w-6 text-[#1E3A8A]" /></div>}
            <div className="text-sm">
              <div className="font-semibold text-slate-800">{companyName || "SilasTech Enterprise"}</div>
              <div className="text-slate-500" data-testid="request-requester">Requested by <b>{user?.name}</b> · {user?.role}</div>
            </div>
            <div className="ml-auto text-sm text-slate-500">Date: <b data-testid="request-date">{fmtDate(new Date().toISOString())}</b></div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 py-2">
            <div className="space-y-1">
              <Label className="text-sm">Purpose</Label>
              <EditableListSelect value={form.purpose} onValueChange={(v) => setForm({ ...form, purpose: v })}
                endpoint="/request-purposes" noun="purpose" canManage={isCEO} placeholder="Select purpose"
                testPrefix="request-purpose" refreshKey={tick} />
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Project (optional)</Label>
              <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="request-project"><SelectValue placeholder="No project" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No project</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Items (up to 5)</Label>
              {form.items.length < 5 && <Button size="sm" variant="outline" data-testid="add-item-row" onClick={addRow} className="gap-1 h-8"><Plus className="h-3.5 w-3.5" /> Add item</Button>}
            </div>
            {form.items.map((it, idx) => {
              const line = (Number(it.quantity) || 0) * (Number(it.unit_cost) || 0);
              return (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`item-row-${idx}`}>
                  <div className="col-span-5">
                    <EditableListSelect value={it.name} onValueChange={(v) => setItem(idx, { name: v })}
                      endpoint="/request-items" noun="item" canManage={isCEO} placeholder={`Item ${idx + 1}`}
                      testPrefix={`request-item-${idx}`} refreshKey={tick} />
                  </div>
                  <Input type="number" min="0" data-testid={`item-qty-${idx}`} className="col-span-2 h-9" placeholder="Qty"
                    value={it.quantity} onChange={(e) => setItem(idx, { quantity: e.target.value })} />
                  <Input type="number" min="0" data-testid={`item-cost-${idx}`} className="col-span-3 h-9" placeholder="Unit cost"
                    value={it.unit_cost} onChange={(e) => setItem(idx, { unit_cost: e.target.value })} />
                  <div className="col-span-1 text-right text-xs font-medium text-slate-600" data-testid={`item-line-${idx}`}>{fmtFCFA(line)}</div>
                  <div className="col-span-1 text-right">
                    {form.items.length > 1 && <button type="button" data-testid={`remove-item-${idx}`} onClick={() => removeRow(idx)} className="text-slate-400 hover:text-red-600"><X className="h-4 w-4" /></button>}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between border-t border-slate-100 pt-3">
            <span className="text-sm text-slate-500">Total requested</span>
            <span className="text-lg font-bold text-[#1E3A8A]" data-testid="request-form-total">{fmtFCFA(formTotal)}</span>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button data-testid="submit-request" onClick={submit} disabled={saving} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">Submit for approval</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View / Review */}
      <Dialog open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setRejecting(false); setReason(""); } }}>
        <DialogContent className="max-w-2xl" data-testid="request-detail-dialog">
          {active && (() => {
            const reviewable = isCEO && active.status === "pending";
            return (
              <>
                <DialogHeader><DialogTitle>{active.request_number} · {active.purpose}</DialogTitle></DialogHeader>
                <div className="flex items-center gap-3 border-b border-slate-100 pb-3 text-sm">
                  {logoUrl ? <img src={logoUrl} alt="logo" className="h-10 w-10 rounded-lg object-cover" /> : <div className="h-10 w-10 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center"><Building2 className="h-5 w-5 text-[#1E3A8A]" /></div>}
                  <div>
                    <div className="text-slate-700">Requested by <b>{active.created_by_name}</b> · {active.created_by_role}</div>
                    <div className="text-slate-500 text-xs">{fmtDate(active.created_at)} · <span className={`border rounded px-1.5 py-0.5 ${STATUS_BADGE[active.status]}`}>{STATUS_LABEL[active.status] || active.status}</span></div>
                  </div>
                </div>
                <div className="space-y-1.5 max-h-72 overflow-auto">
                  {active.items.map((it) => (
                    <div key={it.id} data-testid={`detail-item-${it.id}`} className={`flex items-center gap-2 rounded-md border px-3 py-2 ${it.rejected ? "bg-red-50/60 border-red-100 line-through text-slate-400" : "border-slate-100"}`}>
                      <span className="flex-1 text-sm">{it.name}</span>
                      <span className="text-xs text-slate-500">{it.quantity} × {fmtFCFA(it.unit_cost)}</span>
                      <span className="w-28 text-right text-sm font-medium">{fmtFCFA((Number(it.quantity) || 0) * (Number(it.unit_cost) || 0))}</span>
                      {reviewable && (it.rejected
                        ? <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-500" data-testid={`restore-item-${it.id}`} onClick={() => restoreItem(active.id, it.id)}><RotateCcw className="h-4 w-4" /></Button>
                        : <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" data-testid={`reject-item-${it.id}`} onClick={() => rejectItem(active.id, it.id)}><X className="h-4 w-4" /></Button>)}
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between border-t border-slate-100 pt-3">
                  <span className="text-sm text-slate-500">Total {reviewable ? "(after rejections)" : "requested"}</span>
                  <span className="text-lg font-bold text-[#1E3A8A]" data-testid="detail-total">{fmtFCFA(active.total)}</span>
                </div>
                {active.reconciliation && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-1 text-sm" data-testid="recon-detail">
                    <div className="flex justify-between"><span className="text-slate-500">Requested</span><span className="font-medium">{fmtFCFA(active.reconciliation.requested_amount)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Actually spent</span><span className="font-medium">{fmtFCFA(active.reconciliation.amount_spent)}</span></div>
                    {active.reconciliation.over_spent > 0 && (
                      <div className="flex justify-between text-amber-700"><span>Over-spend (top-up sent to CEO)</span><span className="font-semibold">{fmtFCFA(active.reconciliation.over_spent)}</span></div>
                    )}
                    {active.reconciliation.receipt_url && (
                      <a href={`${BACKEND_URL}${active.reconciliation.receipt_url}`} target="_blank" rel="noreferrer" data-testid="recon-receipt-link" className="inline-flex items-center gap-1 text-[#1E3A8A] hover:underline"><Eye className="h-3.5 w-3.5" /> View receipt</a>
                    )}
                    <div className="text-xs text-slate-400">Reconciled by {active.reconciliation.reconciled_by} · {fmtDate(active.reconciliation.reconciled_at)}</div>
                  </div>
                )}
                {active.status === "debited" && (
                  <p className="text-sm text-red-700" data-testid="detail-debited">Not reconciled in time — {fmtFCFA(active.debited_amount)} debited from salary.</p>
                )}
                {active.reason && <p className="text-sm text-red-600" data-testid="detail-reason">Rejection reason: {active.reason}</p>}
                {rejecting && (
                  <div className="space-y-2"><Label className="text-sm">Reason (optional)</Label>
                    <Textarea data-testid="reject-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} /></div>
                )}
                <DialogFooter>
                  {reviewable ? (
                    rejecting ? (
                      <>
                        <Button variant="outline" onClick={() => setRejecting(false)}>Back</Button>
                        <Button data-testid="confirm-reject-all" onClick={() => rejectAll(active.id)} className="bg-red-600 hover:bg-red-700">Confirm reject</Button>
                      </>
                    ) : (
                      <>
                        <Button variant="outline" data-testid="reject-all" onClick={() => setRejecting(true)} className="text-red-600 gap-1"><X className="h-4 w-4" /> Reject request</Button>
                        <Button data-testid="approve-request" onClick={() => approve(active.id)} className="bg-emerald-600 hover:bg-emerald-700 gap-1"><Check className="h-4 w-4" /> Approve</Button>
                      </>
                    )
                  ) : (
                    <>
                      {isFM && active.status === "approved" && <Button data-testid="execute-in-detail" onClick={() => execute(active.id)} className="bg-emerald-600 hover:bg-emerald-700">Mark executed</Button>}
                      <Button variant="outline" onClick={() => setActive(null)}>Close</Button>
                    </>
                  )}
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
      <Dialog open={!!reconcile} onOpenChange={(o) => { if (!o) { setReconcile(null); setSpent(""); setReceipt(null); } }}>
        <DialogContent data-testid="reconcile-dialog">
          <DialogHeader><DialogTitle>Reconcile {reconcile?.request_number}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Requested amount: <b>{reconcile && fmtFCFA(reconcile.total)}</b>. Upload the receipt to close this request. Purchases not reconciled within 24h are debited from your salary. If you spent more than requested, a top-up request is auto-sent to the CEO.</p>
          <div className="space-y-2">
            <Label className="text-sm">Amount spent (must match receipt)</Label>
            <Input type="number" min="0" data-testid="reconcile-amount" value={spent} onChange={(e) => setSpent(e.target.value)} />
            <Label className="text-sm">Receipt (image or PDF)</Label>
            <Input type="file" data-testid="reconcile-file" onChange={(e) => setReceipt(e.target.files?.[0] || null)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReconcile(null)}>Cancel</Button>
            <Button data-testid="submit-reconcile" onClick={submitReconcile} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">Save reconciliation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showReport} onOpenChange={(o) => { if (!o) { setShowReport(false); setReport(null); } }}>
        <DialogContent className="max-w-3xl" data-testid="recon-report-dialog">
          <DialogHeader><DialogTitle>Monthly reconciliation report</DialogTitle></DialogHeader>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Month</Label>
            <Input type="month" data-testid="report-month" value={reportMonth} onChange={(e) => setReportMonth(e.target.value)} className="w-44" />
          </div>
          {report && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <ReportKpi label="Requested" value={fmtFCFA(report.totals.requested)} />
                <ReportKpi label="Spent" value={fmtFCFA(report.totals.spent)} />
                <ReportKpi label="Over spend" value={fmtFCFA(report.totals.over_spent)} accent="text-amber-700" />
                <ReportKpi label="Salary debited" value={fmtFCFA(report.totals.debited)} accent="text-red-600" />
              </div>
              <div className="max-h-72 overflow-auto rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                    <tr><th className="text-left px-3 py-2">Request</th><th className="text-left px-3 py-2">By</th><th className="text-right px-3 py-2">Requested</th><th className="text-right px-3 py-2">Spent</th><th className="text-left px-3 py-2">Status</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {report.items.map((r) => (
                      <tr key={r.id} data-testid={`report-row-${r.id}`} className="hover:bg-slate-50">
                        <td className="px-3 py-2 font-medium text-slate-700">{r.request_number}</td>
                        <td className="px-3 py-2 text-slate-500">{r.created_by_name}</td>
                        <td className="px-3 py-2 text-right">{fmtFCFA(r.requested)}</td>
                        <td className="px-3 py-2 text-right">{r.spent ? fmtFCFA(r.spent) : "—"}</td>
                        <td className="px-3 py-2"><span className={`text-xs border rounded px-2 py-0.5 ${STATUS_BADGE[r.status]}`}>{STATUS_LABEL[r.status] || r.status}</span></td>
                      </tr>
                    ))}
                    {report.items.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400" data-testid="report-empty">No executed purchases this month.</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setShowReport(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
const ReportKpi = ({ label, value, accent }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2" data-testid={`report-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
    <div className={`text-sm font-bold ${accent || "text-slate-800"}`}>{value}</div>
  </div>
);
